// src/components/common/BaseContextMenu.tsx
import {
  Category as CategoryIcon,
  ContentCopy as CopyIcon,
  ExitToApp as NavigateIcon,
  OpenInNew as OpenInNewIcon,
  PlusOne,
} from "@mui/icons-material";
import DoneIcon from "@mui/icons-material/Done";
import { Box, Divider, Menu, MenuItem } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MyCustomerApiResponse } from "../../../store/services/customerService";
import CompleteActionDialog from "../../activity-complete-dialog/CompleteActionDialog";
import MoveWorkItemDialog from "../../activity-move-dialog/MoveWorkItemDialog";
import UpdateCustomerSegmentDialog from "../../dialogs/UpdateCustomerSegmentDialog";
import { NextAction } from "../../next-action/types";
import VisualActionDialog from "../../next-action/VisualActionDialog";
import { useActivityPanel } from "../../../hooks/useActivityPanel";

// Base actions that all context menus should support
export type BaseAction = "viewDetails" | "copyId" | "copyName";

// Basic context menu props
export interface BaseContextMenuProps {
  contextMenu: {
    mouseX: number;
    mouseY: number;
    customer: MyCustomerApiResponse;
  } | null;
  onClose: () => void;
  onAction: (action: BaseAction | string) => void;
  additionalMenuItems?: React.ReactNode;
}

type Dialogs =
  | "updateSegment"
  | "createActivity"
  | "completeActivity"
  | "moveActivity";

/**
 * Base context menu with common functionality that can be extended
 * by more specific context menus
 */
const BaseContextMenu: React.FC<BaseContextMenuProps> = ({
  contextMenu,
  onClose,
  onAction,
  additionalMenuItems,
}) => {
  const navigate = useNavigate();
  const { openActivity } = useActivityPanel();
  const [dialog, setDialog] = useState<Dialogs | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    null,
  );

  const [selectedWorkItemNo, setSelectedWorkItemNo] = useState<number | null>(
    null,
  );

  const handleDialog = (type: Dialogs) => {
    if (contextMenu) {
      setDialog(type);
      setSelectedCustomerId(contextMenu.customer.Id);
      if (
        (type === "completeActivity" || type === "moveActivity") &&
        contextMenu.customer.NextAction
      ) {
        setSelectedWorkItemNo(contextMenu.customer.NextAction.ActionWorkItemNo);
      }

      onClose(); // Close context menu when opening a dialog
    }
  };
  const handleOpenActivity = () => {
    if (contextMenu?.customer?.NextAction?.ActionWorkItemNo) {
      openActivity(contextMenu.customer.NextAction.ActionWorkItemNo);
      onClose();
    }
  };

  const handleCompleteActivity = () => {
    if (contextMenu?.customer?.NextAction?.ActionWorkItemNo) {
      openActivity(
        contextMenu.customer.NextAction.ActionWorkItemNo,
        "complete",
      );
      onClose();
    }
  };

  const handleNextActionChange = (newAction: NextAction) => {
    console.log("Ny aktivitet skapad:", newAction);
    // Optionally trigger refetch or other updates
  };

  const onActionClicked = (action: BaseAction | string) => {
    if (contextMenu?.customer === undefined) return;
    console.log(contextMenu.customer);
    switch (action) {
      case "viewDetails":
        // Navigate to details
        navigate(`/customer/${contextMenu.customer.Id}`);
        break;
      case "copyId":
        navigator.clipboard.writeText(contextMenu.customer.CustomerId);
        break;
      case "copyName":
        navigator.clipboard.writeText(contextMenu.customer.Name);
        break;
    }
    onAction(action);
  };

  // Handle when any dialog closes
  const handleActionDialogClose = () => {
    setDialog(null);
    setSelectedCustomerId(null);
    setSelectedWorkItemNo(null);
  };
  const haveActivities = !!contextMenu?.customer?.NextAction;

  return (
    <>
      <Menu
        open={contextMenu !== null}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <Box>
          {/* Common menu items present in all context menus */}
          <MenuItem onClick={() => onActionClicked("viewDetails")}>
            <NavigateIcon fontSize="small" sx={{ mr: 1 }} />
            Visa kunddetaljer
          </MenuItem>
          <Divider />

          {/* Copy section - common functionality */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              px: 0,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <MenuItem
              onClick={() => onActionClicked("copyId")}
              sx={{ py: 1, flex: 1, borderRadius: 0 }}
              disabled={!contextMenu?.customer.CustomerId}
            >
              <CopyIcon fontSize="small" sx={{ mr: 1 }} />
              Knr
            </MenuItem>
            <Divider orientation="vertical" flexItem />
            <MenuItem
              onClick={() => onActionClicked("copyName")}
              sx={{ py: 1, flex: 1, borderRadius: 0 }}
            >
              <CopyIcon fontSize="small" sx={{ mr: 1 }} />
              Namn
            </MenuItem>
          </Box>
          <Divider />
          <MenuItem onClick={() => handleDialog("updateSegment")}>
            <CategoryIcon fontSize="small" sx={{ mr: 1 }} />
            Ändra segment
          </MenuItem>
          {haveActivities ? (
            [
              <MenuItem key="open" onClick={handleOpenActivity}>
                <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} />
                Öppna aktivitet
              </MenuItem>,
              <MenuItem key="complete" onClick={handleCompleteActivity}>
                <DoneIcon fontSize="small" sx={{ mr: 1 }} />
                Slutför aktivitet
              </MenuItem>,
            ]
          ) : (
            <MenuItem onClick={() => handleDialog("createActivity")}>
              <PlusOne fontSize="small" sx={{ mr: 1 }} />
              Skapa en aktivitet
            </MenuItem>
          )}
          {/* Custom actions added by the specific implementation */}
          {additionalMenuItems}
        </Box>
      </Menu>

      {dialog === "updateSegment" && selectedCustomerId !== null && (
        <UpdateCustomerSegmentDialog
          open={true}
          onClose={handleActionDialogClose}
          customerIds={[selectedCustomerId]}
        />
      )}

      {dialog === "createActivity" && selectedCustomerId !== null && (
        <VisualActionDialog
          open={true}
          customerNo={selectedCustomerId}
          onClose={handleActionDialogClose}
          onNextActionChange={handleNextActionChange}
        />
      )}

      {dialog === "completeActivity" &&
        selectedWorkItemNo &&
        selectedCustomerId !== null && (
          <CompleteActionDialog
            open={true}
            onClose={handleActionDialogClose}
            customerNo={selectedCustomerId}
            onComplete={handleActionDialogClose}
            workItemNo={selectedWorkItemNo}
          />
        )}

      {dialog === "moveActivity" &&
        selectedWorkItemNo &&
        selectedCustomerId !== null && (
          <MoveWorkItemDialog
            open={true}
            onClose={handleActionDialogClose}
            onComplete={handleActionDialogClose}
            workItemNo={selectedWorkItemNo}
          />
        )}
    </>
  );
};

export default BaseContextMenu;
